import { c as _c } from "react/compiler-runtime";
import Grid from '@precis-digital/kurama/Grid';
import { styled } from '@precis-digital/kurama/utils';
import { TooltipBody, TooltipTitle } from '@precis-digital/kurama/Tooltip';
import { type RechartsTooltipProps as TooltipProps } from '@precis-digital/kurama/recharts';
import { zIndexes } from '@precis-digital/kurama/theme';
import { type ReactElement } from 'react';
import { useChartElementContext } from '../hooks/chartElementContext';
import { useChartCardDefaults } from '../hooks/useChartCardDefaults';
import { useDeepCompareMemoize } from '../hooks/generics';
import type { ElementsDescriptionsMap, FirstOrThirdAxisDetails, RealTooltipPayload, SecondAxisDetails } from '../internalTypes';
import type { ChartCardTFunction, ChartDataRow, ChartSelections, DrawingColorMaps } from '../types';
import { getTooltipBodyElements, getTooltipTitle } from '../utils/tooltip';
type DefaultTooltipContentProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>> = TooltipProps<string | number, string> & {
  chartProps: {
    firstAxisDetails: FirstOrThirdAxisDetails<DataKey, DataRow>;
    secondAxisDetails: Array<SecondAxisDetails<DataKey, DataRow>>;
    thirdAxisDetails: FirstOrThirdAxisDetails<DataKey, DataRow> | undefined;
    elementsDescriptionsMap: ElementsDescriptionsMap<DataKey>;
    drawingColorMaps: DrawingColorMaps;
    selections: ChartSelections<DataKey>;
    t: ChartCardTFunction;
  };
  payload?: Array<RealTooltipPayload<DataKey, DataRow>>;
};
const DefaultTooltipContent = props => {
  const $ = _c(29);
  const {
    firstAxisDetails,
    secondAxisDetails,
    thirdAxisDetails,
    elementsDescriptionsMap,
    drawingColorMaps,
    selections,
    t
  } = props.chartProps;
  const {
    active,
    payload: payloadProp
  } = props;
  const {
    chartAxisIdsAndDomains: t0
  } = useChartElementContext();
  const {
    firstAxisDomain,
    secondAxisDomain,
    otherSecondAxisDomain
  } = t0;
  let t1;
  if ($[0] !== payloadProp) {
    t1 = payloadProp ?? [];
    $[0] = payloadProp;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const payload = useDeepCompareMemoize(t1);
  const chartType = secondAxisDetails[0].chartType;
  let t2;
  if ($[2] !== elementsDescriptionsMap || $[3] !== firstAxisDetails || $[4] !== payload || $[5] !== selections || $[6] !== t || $[7] !== thirdAxisDetails) {
    t2 = getTooltipTitle({
      payload,
      firstAxisDetails,
      elementsDescriptionsMap,
      thirdAxisDetails,
      selections,
      t
    });
    $[2] = elementsDescriptionsMap;
    $[3] = firstAxisDetails;
    $[4] = payload;
    $[5] = selections;
    $[6] = t;
    $[7] = thirdAxisDetails;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const title = t2;
  const defaults = useChartCardDefaults();
  let t3;
  if ($[9] !== chartType || $[10] !== defaults || $[11] !== drawingColorMaps || $[12] !== elementsDescriptionsMap || $[13] !== firstAxisDetails || $[14] !== firstAxisDomain || $[15] !== otherSecondAxisDomain || $[16] !== payload || $[17] !== secondAxisDetails || $[18] !== secondAxisDomain || $[19] !== selections || $[20] !== t) {
    t3 = getTooltipBodyElements({
      defaults,
      chartType,
      payload,
      firstAxisDetails,
      firstAxisDomain,
      secondAxisDetails,
      secondAxisDomain,
      otherSecondAxisDomain,
      elementsDescriptionsMap,
      drawingColorMaps,
      selections,
      t
    });
    $[9] = chartType;
    $[10] = defaults;
    $[11] = drawingColorMaps;
    $[12] = elementsDescriptionsMap;
    $[13] = firstAxisDetails;
    $[14] = firstAxisDomain;
    $[15] = otherSecondAxisDomain;
    $[16] = payload;
    $[17] = secondAxisDetails;
    $[18] = secondAxisDomain;
    $[19] = selections;
    $[20] = t;
    $[21] = t3;
  } else {
    t3 = $[21];
  }
  const bodyElements = t3;
  if (active !== true || payload.length === 0) {
    return null;
  }
  let t4;
  if ($[22] !== title) {
    t4 = <TooltipTitle text={title} />;
    $[22] = title;
    $[23] = t4;
  } else {
    t4 = $[23];
  }
  let t5;
  if ($[24] !== bodyElements) {
    t5 = <TooltipBody><Grid container={true} flexDirection="column">{bodyElements}</Grid></TooltipBody>;
    $[24] = bodyElements;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  let t6;
  if ($[26] !== t4 || $[27] !== t5) {
    t6 = <StyledTooltipContainer minWidth="350px"><StyledTooltipContent>{t4}{t5}</StyledTooltipContent></StyledTooltipContainer>;
    $[26] = t4;
    $[27] = t5;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  return t6;
};
const StyledTooltipContainer = styled(Grid, {
  shouldForwardProp: prop => prop !== 'minWidth' && prop !== 'maxWidth'
})<{
  minWidth?: string;
  maxWidth?: string;
}>(({
  theme,
  minWidth,
  maxWidth
}) => ({
  backgroundColor: theme.tokens.background.main.inverse.default,
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  minWidth: minWidth ?? 'none',
  maxWidth: maxWidth ?? 'none',
  wordBreak: 'break-word',
  zIndex: zIndexes.chartTooltip,
  color: theme.tokens.foreground.main.inverse,
  borderRadius: theme.borderRadius.xLarge
}));
const StyledTooltipContent = styled(Grid)(({
  theme
}) => ({
  padding: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  gap: theme.spacing(3),
  width: '100%'
}));
export default DefaultTooltipContent;